import { ref, readonly, Ref } from 'vue';

import {
  dispatchGetMSdsSection2Categories,
  dispatchGetMSdsSection2Items,
  dispatchGetMSdsSection2Hazards,
} from '@/store/main/actionsMain';

import type {
  MSdsSection2Category,
  MSdsSection2Item,
  MSdsSection2Hazard,
} from '@/interfaces/api/general/master';
import store from '@/store';
import { Section2ClassificationSystemId } from '@/lib/enum/Section2ClassificationSystemId';

type MSection2 = {
  categories: Record<number, MSdsSection2Category>;
  items: Record<number, MSdsSection2Item>;
  hazards: Record<number, MSdsSection2Hazard>;
};
const categoriesRef = ref<MSection2['categories']>({});
const itemsRef = ref<MSection2['items']>({});
const hazardsRef = ref<MSection2['hazards']>({});

const loadedRef = ref(false);
const loadingRef = ref(false);
const errorRef = ref<unknown>(null);

let inFlight: Promise<void> | null = null;

async function fetchAll() {
  const [categories, items, hazards] = await Promise.all([
    dispatchGetMSdsSection2Categories(store),
    dispatchGetMSdsSection2Items(store),
    dispatchGetMSdsSection2Hazards(store),
  ]);

  categoriesRef.value = categories ?? {};
  itemsRef.value = items ?? {};
  hazardsRef.value = hazards ?? {};
}
export type ClassificationSystemKey = 'GHS' | 'HCS' | 'CLP' | 'JIS';
export type ReservedIds = {
  CATEGORY_NOT_CATEGORIZABLE: number;
  CATEGORY_DEFAULT: number;
  HAZARD_DEFAULT: number;

  ITEM_PHYSICOCHEMICALS: readonly number[];
  ITEM_HEALTH_HAZARDS: readonly number[];
  ITEM_ENVIRONMENTAL_HAZARDS: readonly number[];
  ITEM_HAS_HAZARDS: readonly number[];
  ITEM_CLASSIFICATION_SYSTEM: {
    readonly [K in ClassificationSystemKey]: {
      includes: readonly number[];
      excludes?: readonly number[];
    };
  };
};

export type UseMSdsSection2Return = {
  categories: Readonly<Ref<Record<number, MSdsSection2Category>>>;
  items: Readonly<Ref<Record<number, MSdsSection2Item>>>;
  hazards: Readonly<Ref<Record<number, MSdsSection2Hazard>>>;

  loaded: Readonly<Ref<boolean>>;
  loading: Readonly<Ref<boolean>>;
  error: Readonly<Ref<unknown>>;

  load: () => Promise<void>;
  refresh: () => Promise<void>;

  reserved_ids: ReservedIds;
  getItemsByClassificationSystemId: (
    system: ClassificationSystemKey
  ) => MSdsSection2Item[];
  getSection2CategoriesByItemId: (itemId: number) => MSection2['categories'];
};

const reserved_ids: ReservedIds = {
  // category
  CATEGORY_NOT_CATEGORIZABLE: 9999999,
  CATEGORY_DEFAULT: 0,

  // hazard
  HAZARD_DEFAULT: 0,

  // item
  ITEM_PHYSICOCHEMICALS: [
    1000000, // EXPLOSIVES
    1001000, // FLAMMABLE_GASES
    1002000, // AEROSOLS
    1003000, // OXIDIZING_GASES
    1004000, // GASES_UNDER_PRESSURE
    1005000, // FLAMMABLE_LIQUIDS
    1006000, // FLAMMABLE_SOLIDS
    1007000, // SELF_REACTIVE_SUBSTANCES_AND_MIXTURES
    1008000, // PYROPHORIC_LIQUIDS
    1009000, // PYROPHORIC_SOLIDS
    1010000, // SELF_HEATING_SUBSTANCES_AND_MIXTURES
    1011000, // SUBSTANCES_AND_MIXTURES
    1012000, // OXIDIZING_LIQUIDS
    1013000, // OXIDIZING_SOLIDS
    1014000, // ORGANIC_PEROXIDES
    1015000, // CORROSIVE_TO_METALS
    1016000, // DESENSITIZED_EXPLOSIVES
    1001500, // PYROPHORIC_GASES
    1015500, // COMBUSTIBLE_DUST
    1002500, // CHEMICALS_UNDER_PRESSURE
  ],
  ITEM_HEALTH_HAZARDS: [
    1017000, // ACUTE_TOXICITY_ORAL
    1018000, // ACUTE_TOXICITY_DERMAL
    1019000, // ACUTE_TOXICITY_INHALATION_GASES
    1020000, // ACUTE_TOXICITY_INHALATION_VAPOURS
    1021000, // ACUTE_TOXICITY_INHALATION_DUSTS
    1022000, // ACUTE_TOXICITY_INHALATION_MISTS
    1023000, // SKIN_CORROSION_IRRITATION
    1024000, // SERIOUS_EYE_DAMAGE
    1025000, // RESPIRATORY_SENSITIZATION
    1026000, // SKIN_SENSITIZATION
    1027000, // GERM_CELL_MUTAGENICITY
    1028000, // CARCINOGENICITY
    1029000, // REPRODUCTIVE_TOXICITY
    1030000, // SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE
    1031000, // SPECIFIC_TARGET_ORGAN_TOXICITY_REPEATED_EXPOSURE
    1032000, // ASPIRATION_HAZARD
    1032500, // SIMPLE_ASPHYXIANT
    1032600, // ENDOCRINE_DISRUPTING_HUMAN_HEALTH
    1022500, // ACUTE TOXICITY
  ],
  ITEM_ENVIRONMENTAL_HAZARDS: [
    1033000, // HAZARDOUS_TO_THE_AQUATIC_ENVIRONMENT_ACUTE
    1034000, // HAZARDOUS_TO_THE_AQUATIC_ENVIRONMENT_LONG_TERM
    1035000, // HAZARDOUS_TO_THE_OZONE_LAYER
    1037000, // ENDOCRINE_DISRUPTING_ENVIRONMENT
    1038000, // PBT
    1039000, // VPVB
    1040000, // PMT
    1041000, // VPVM
  ],
  ITEM_HAS_HAZARDS: [
    1029000, // REPRODUCTIVE_TOXICITY
    1030000, // SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE
    1031000, // SPECIFIC_TARGET_ORGAN_TOXICITY_REPEATED_EXPOSURE
  ],
  ITEM_CLASSIFICATION_SYSTEM: {
    GHS: {
      includes: [],
    },
    HCS: {
      includes: [],
    },
    CLP: {
      includes: [],
      excludes: [],
    },
    JIS: {
      includes: [],
    },
  },
};

export function useMSdsSection2(): UseMSdsSection2Return {
  const load = async (opts?: { force?: boolean }) => {
    const force = opts?.force === true;

    if (!force && loadedRef.value) return;

    if (!force && inFlight) return inFlight;

    loadingRef.value = true;
    errorRef.value = null;

    inFlight = (async () => {
      try {
        await fetchAll();
        loadedRef.value = true;
      } catch (e) {
        errorRef.value = e;
        throw e;
      } finally {
        loadingRef.value = false;
        inFlight = null;
      }
    })();

    return inFlight;
  };

  const refresh = () => load({ force: true });
  const getItemsByClassificationSystemId = (
    system: ClassificationSystemKey
  ) => {
    const allItems = Object.values(itemsRef.value);
    // "OTHER" => include all
    if (system === Section2ClassificationSystemId.OTHER.id) {
      return allItems;
    }

    const scopedIdSet = new Set<number>(
      Object.values(reserved_ids.ITEM_CLASSIFICATION_SYSTEM).flatMap(
        (cfg) => cfg.includes
      )
    );

    const allowedSet = new Set<number>(
      reserved_ids.ITEM_CLASSIFICATION_SYSTEM[system].includes
    );

    const excludedSet = new Set<number>(
      reserved_ids.ITEM_CLASSIFICATION_SYSTEM[system].excludes ?? []
    );

    return allItems.filter((item) => {
      if (excludedSet.has(item.id)) {
        return false;
      }

      if (!scopedIdSet.has(item.id)) {
        return true;
      }

      return allowedSet.has(item.id);
    });
  };

  const getSection2CategoriesByItemId = (
    itemId: number
  ): MSdsSection2Category[] => {
    const allCategories = Object.values(categoriesRef.value);

    const itemCategories = allCategories.filter((c) => c.item_id === itemId);

    const isBinary =
      itemCategories.length > 0 &&
      itemCategories.every((c) =>
        ['該当', '非該当', '該非不明確', '該非記載なし'].includes(c.name_ja)
      );

    const defaultCat = allCategories.find(
      (c) => c.id === reserved_ids.CATEGORY_DEFAULT
    );

    const notCategorizableCat = allCategories.find(
      (c) => c.id === reserved_ids.CATEGORY_NOT_CATEGORIZABLE
    );

    return [
      ...(defaultCat ? [defaultCat] : []),
      ...(!isBinary && notCategorizableCat ? [notCategorizableCat] : []),
      ...itemCategories,
    ];
  };

  return {
    // data
    categories: readonly(categoriesRef),
    items: readonly(itemsRef),
    hazards: readonly(hazardsRef),

    // state
    loaded: readonly(loadedRef),
    loading: readonly(loadingRef),
    error: readonly(errorRef),
    // actions
    load,
    refresh,

    // utils
    reserved_ids,
    getItemsByClassificationSystemId,
    getSection2CategoriesByItemId,
  };
}
